import { z } from 'zod/v4';

export const CompanySizeOptions = ['1-25', '26-250', '251-1000', '1000+'] as const;
export type CompanySize = (typeof CompanySizeOptions)[number];

export const RegionOptions = [
  'North America',
  'Americas - Other',
  'Europe & Africa',
  'APAC',
  'Middle East',
] as const;
export type Region = (typeof RegionOptions)[number];

export const ProductInterestOptions = ['Event Lead Capture', 'Digital Business Cards'] as const;
export type ProductInterest = (typeof ProductInterestOptions)[number];

export const EventAttendanceOptions = ['1-5', '6-10', '11-20', '21-50', '51-100', '100+'] as const;
export type EventAttendance = (typeof EventAttendanceOptions)[number];

export const DbcAmountOptions = [
  '1-10',
  '11-50',
  '51-100',
  '101-250',
  '251-1000',
  '1000+',
] as const;
export type DbcAmount = (typeof DbcAmountOptions)[number];

// The array values are the exact strings HubSpot's `how_did_you_hear_about_blinq_` property
// expects; the HearAboutUsLabels map holds the user-facing copy for each option.
export const HearAboutUsOptions = [
  'Search engine',
  'AI Search (ChatGPT, Claude etc)',
  'LinkedIn',
  'Review Site (G2, Trustpilot)',
  'Event',
  'Colleague / Manager',
  'From an existing Blinq customer',
  'Advertising',
  'Friend',
] as const;
export type HearAboutUs = (typeof HearAboutUsOptions)[number];

export const HearAboutUsLabels: Record<HearAboutUs, string> = {
  'Search engine': 'Google / Search engine',
  'AI Search (ChatGPT, Claude etc)': 'AI Search (ChatGPT, Claude etc.)',
  LinkedIn: 'LinkedIn',
  'Review Site (G2, Trustpilot)': 'Review site (G2, Trustpilot)',
  Event: 'At an event',
  'Colleague / Manager': 'Colleague / Manager',
  'From an existing Blinq customer': 'From an existing Blinq customer',
  Advertising: 'Advertising',
  Friend: 'Friend',
};

export const step1Schema = z.object({
  step: z.literal(1),
  email: z.email('Please enter a valid email address.'),
});

export const step2Schema = z
  .object({
    step: z.literal(2),
    email: z.email('Please enter a valid email address.'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    jobTitle: z.string().min(1, 'Job title is required'),
    companyName: z.string().min(1, 'Company name is required'),
    companySize: z.enum(CompanySizeOptions, { error: 'Company size is required' }),
    region: z.enum(RegionOptions, { error: 'Please select a region' }),
    productInterest: z.array(z.enum(ProductInterestOptions)).min(1, 'Select at least one product'),
    eventAttendance: z.enum(EventAttendanceOptions).optional(),
    dbcAmount: z.enum(DbcAmountOptions).optional(),
    hearAboutUs: z
      .array(z.enum(HearAboutUsOptions))
      .min(1, 'Please tell us how you heard about Blinq'),
  })
  .superRefine((data, ctx) => {
    if (data.productInterest.includes('Event Lead Capture') && !data.eventAttendance) {
      ctx.addIssue({
        code: 'custom',
        path: ['eventAttendance'],
        message: 'Please select how many events you attend',
      });
    }
    if (data.productInterest.includes('Digital Business Cards') && !data.dbcAmount) {
      ctx.addIssue({
        code: 'custom',
        path: ['dbcAmount'],
        message: 'Please select how many employees need cards',
      });
    }
  });

export const contactSalesSchema = z.union([step1Schema, step2Schema]);

export type Step1Data = z.infer<typeof step1Schema>;
export type Step2Data = z.infer<typeof step2Schema>;
export type ContactSalesData = z.infer<typeof contactSalesSchema>;

export const getBookingRegion = (countryCode: string | null): Region | undefined => {
  const regionData: { region: Region; countries: string[] }[] = [
    {
      region: 'North America',
      countries: ['US', 'CA'],
    },
    {
      region: 'Americas - Other',
      countries: [
        'MX',
        'BR',
        'AR',
        'CL',
        'CO',
        'PE',
        'VE',
        'GT',
        'CR',
        'PA',
        'UY',
        'BO',
        'PY',
        'SV',
        'HN',
        'NI',
      ],
    },
    {
      region: 'Europe & Africa',
      countries: [
        'GB',
        'FR',
        'DE',
        'IT',
        'ES',
        'NL',
        'BE',
        'LU',
        'IE',
        'DK',
        'SE',
        'NO',
        'FI',
        'PL',
        'CZ',
        'HU',
        'AT',
        'CH',
        'PT',
        'GR',
        'RO',
        'BG',
        'HR',
        'SI',
        'SK',
        'EE',
        'LV',
        'LT',
        'IS',
      ],
    },
    {
      region: 'APAC',
      countries: [
        'JP',
        'CN',
        'IN',
        'AU',
        'NZ',
        'KR',
        'SG',
        'MY',
        'TH',
        'PH',
        'ID',
        'VN',
        'TW',
        'HK',
      ],
    },
    {
      region: 'Middle East',
      countries: ['AE', 'SA', 'QA', 'KW', 'OM', 'BH', 'EG', 'IQ', 'JO', 'LB', 'YE'],
    },
  ];

  if (!countryCode) {
    return;
  }

  return regionData.find(({ countries }) => countries.includes(countryCode))?.region;
};
